import Shepherd from 'shepherd.js';

export type ShepherdTour = InstanceType<typeof Shepherd.Tour>;
export type TourDefinition = () => ShepherdTour;
export type TrackableTour = ShepherdTour & {
  __tourStartDate?: string | null;
};

export function markTourStartDate(tour: ShepherdTour): void {
  const trackableTour = tour as TrackableTour;
  if (trackableTour.__tourStartDate === null || typeof trackableTour.__tourStartDate === 'undefined') {
    trackableTour.__tourStartDate = new Date().toISOString();
  }
}

export function setButtonLoading(tour: ShepherdTour, loading: boolean): void {
  const currentStep = tour.getCurrentStep();
  if (!currentStep) return;
  const btn = currentStep.getElement()?.querySelector('.shepherd-footer .shepherd-button') as HTMLButtonElement;
  if (!btn) return;

  if (loading) {
    btn.disabled = true;
    btn.dataset.originalText = btn.innerHTML;
    btn.innerHTML = '<span class="tour-spinner"></span> Loading...';
  } else {
    btn.disabled = false;
    if (btn.dataset.originalText) {
      btn.innerHTML = btn.dataset.originalText;
    }
  }
}

export function injectStepUI(stepEl: Element | null | undefined, stepNumber: number, totalSteps: number): void {
  if (!stepEl) return;

  const footer = stepEl.querySelector('.shepherd-footer');
  if (footer) {
    const existingCounter = footer.querySelector('.tour-step-counter-footer');
    if (existingCounter) existingCounter.remove();

    const counter = document.createElement('div');
    counter.className = 'tour-step-counter-footer';
    counter.textContent = `${stepNumber} of ${totalSteps}`;
    footer.insertBefore(counter, footer.firstChild);
  }

  const header = stepEl.querySelector('.shepherd-header');
  if (header) {
    const existingTag = header.querySelector('.tour-feature-tag');
    if (existingTag) existingTag.remove();

    const tag = document.createElement('div');
    tag.className = 'tour-feature-tag';
    tag.textContent = 'FEATURE HIGHLIGHT';
    header.insertBefore(tag, header.firstChild);
  }
}

function pollUntil(condition: () => boolean, intervalMs = 100, timeoutMs = 10000): Promise<void> {
  return new Promise<void>((resolve) => {
    const startedAt = Date.now();
    const poll = () => {
      if (condition() || Date.now() - startedAt > timeoutMs) {
        resolve();
        return;
      }
      setTimeout(poll, intervalMs);
    };
    poll();
  });
}

export function waitForElement(selector: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => !!document.querySelector(selector), 100, timeoutMs);
}

export function waitForEnabledSelect(labelSelector: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const label = document.querySelector(labelSelector);
    const vSelect = label?.closest('.selection-step')?.querySelector('.v-select');
    return !!label && !vSelect?.classList.contains('vs--disabled');
  }, 200, timeoutMs);
}

function getVueInstance(el: Element | null): any {
  return el ? (el as any).__vue__ : null;
}

function getVSelectInContainer(containerSelector: string): any {
  const container = document.querySelector(containerSelector);
  if (!container) return null;
  const vSelectEl = container.classList?.contains('v-select')
    ? container
    : container.querySelector('.v-select');
  return getVueInstance(vSelectEl);
}

function getVSelectByLabelFor(forValue: string): any {
  const label = document.querySelector(`label[for="${forValue}"]`);
  const vSelectEl = label?.closest('.selection-step')?.querySelector('.v-select') ?? null;
  return getVueInstance(vSelectEl);
}

function selectVSelectOption(instance: any, labelProp: string, value: string): boolean {
  if (!instance?.options?.length) return false;

  const upperValue = value.toUpperCase();
  const option = instance.options.find((o: any) => {
    const optionValue: string = labelProp ? o[labelProp] : (typeof o === 'string' ? o : '');
    return optionValue?.toUpperCase().includes(upperValue);
  });

  if (!option) return false;

  instance.select(option);
  instance.open = false;
  instance.$refs?.search?.blur();
  return true;
}

export function autoSelectInContainer(containerSelector: string, labelProp: string, value: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const instance = getVSelectInContainer(containerSelector);
    return selectVSelectOption(instance, labelProp, value);
  }, 200, timeoutMs);
}

export function autoSelectByLabel(forValue: string, labelProp: string, value: string, timeoutMs = 10000): Promise<void> {
  return pollUntil(() => {
    const instance = getVSelectByLabelFor(forValue);
    return selectVSelectOption(instance, labelProp, value);
  }, 200, timeoutMs);
}

function getTruckSelectorInstance(): any {
  const el = document.querySelector('.truck-selection-embedded') || document.querySelector('.truck-selection-page');
  return getVueInstance(el);
}

function collectBranchIds(nodes: any[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.hasChildren) {
      ids.push(node.id);
      if (node.children?.length) {
        ids.push(...collectBranchIds(node.children));
      }
    }
  }
  return ids;
}

export function expandAllTreeNodes(): void {
  const instance = getTruckSelectorInstance();
  if (!instance?.displayedComponents) return;

  const allIds = collectBranchIds(instance.displayedComponents);
  for (const id of allIds) {
    if (!instance.expandedComponentIds.includes(id)) {
      instance.expandedComponentIds.push(id);
    }
  }
}

function findLeafByLabel(nodes: any[], label: string): any {
  const upperLabel = label.toUpperCase();
  for (const node of nodes) {
    const nodeLabel = (node.componentDescription || node.name || '').toUpperCase();
    if (!node.hasChildren && nodeLabel.includes(upperLabel)) {
      return node;
    }
    if (node.children?.length) {
      const found = findLeafByLabel(node.children, label);
      if (found) return found;
    }
  }
  return null;
}

export function findLeafElement(leafLabel: string): HTMLElement | null {
  const rows = document.querySelectorAll<HTMLElement>('.tree-node__row.tree-node__row--leaf');
  const upperLabel = leafLabel.toUpperCase();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.textContent?.toUpperCase().includes(upperLabel)) {
      return row;
    }
  }
  return null;
}

export function selectLeafComponent(leafLabel: string): void {
  const instance = getTruckSelectorInstance();
  if (!instance?.displayedComponents) return;

  const leaf = findLeafByLabel(instance.displayedComponents, leafLabel);
  if (leaf) {
    instance.ToggleComponent(leaf);
  }
}